import React from "react";
import {
  contributeAiCapabilityParamOption,
  useProvideAiCapability,
} from "../../ai-omnibar/ai-capability-registry";

type NavigableSection = {
  label: string;
  subsections: { label: string }[];
};

type Params<TSection extends NavigableSection> = {
  /** Capability id declared by the profile module, e.g. "patient.section.open". */
  capabilityId: string;
  /**
   * The sections actually on screen. Already filtered by the modal for permissions, billing
   * feature, onboarding agency and record status - which is why the options have to come from here
   * rather than from the static section list: the model should only ever be offered sections this
   * user can see for this record.
   */
  sections: TSection[];
  navigation: {
    handleSectionChange: (index: number) => void;
    handleSubsectionChange: (index: number) => void;
  };
  /** False while the record is still loading. */
  enabled: boolean;
};

/**
 * Section navigation for a profile modal, offered to the AI omnibar.
 *
 * Shared by the patient and caregiver profiles because the mechanism is identical: contribute one
 * option per visible subsection while mounted, and resolve the model's choice back to the indices
 * the navigation hook expects.
 */
export function useEntityProfileSectionAiCapability<TSection extends NavigableSection>(
  params: Params<TSection>
): void {
  const { capabilityId, sections, navigation, enabled } = params;

  const sectionsRef = React.useRef(sections);
  sectionsRef.current = sections;

  const navigationRef = React.useRef(navigation);
  navigationRef.current = navigation;

  // Contribute the visible sections as the enum's options, and take them away on unmount so a
  // closed profile leaves nothing behind in the manifest.
  const optionsKey = sections
    .map((section) => `${section.label}:${section.subsections.map((s) => s.label).join(",")}`)
    .join("|");

  React.useEffect(() => {
    if (!enabled) {
      return;
    }

    const releases = sectionsRef.current.flatMap((section) =>
      section.subsections.map((subsection) =>
        contributeAiCapabilityParamOption(capabilityId, "section", {
          value: `${section.label}/${subsection.label}`,
          label:
            section.label === subsection.label
              ? section.label
              : `${section.label} > ${subsection.label}`,
        })
      )
    );

    return () => releases.forEach((release) => release());
  }, [capabilityId, enabled, optionsKey]);

  useProvideAiCapability(
    capabilityId,
    (args) => {
      const requested = String(args.section);
      const [rawSection, rawSubsection] = requested.split("/");
      const wanted = (rawSubsection ?? rawSection).trim().toLowerCase();
      const wantedSection = rawSection.trim().toLowerCase();

      // The model is given "Section/Subsection" values, but it sometimes answers with just the part
      // it heard ("authorizations"). Match on either, case-insensitively, before giving up.
      const sectionIndex = sectionsRef.current.findIndex(
        (section) =>
          section.label.toLowerCase() === wantedSection ||
          section.subsections.some((subsection) => subsection.label.toLowerCase() === wanted)
      );

      if (sectionIndex === -1) {
        throw new Error(
          `This profile has no "${requested}" section open to you. It has: ${sectionsRef.current
            .map((section) => section.label)
            .join(", ")}.`
        );
      }

      navigationRef.current.handleSectionChange(sectionIndex);

      const subsectionIndex = sectionsRef.current[sectionIndex].subsections.findIndex(
        (subsection) => subsection.label.toLowerCase() === wanted
      );

      if (subsectionIndex > 0) {
        navigationRef.current.handleSubsectionChange(subsectionIndex);
      }

      const section = sectionsRef.current[sectionIndex];
      const subsection = section.subsections[subsectionIndex];

      return subsection === undefined || subsection.label === section.label
        ? `Showing ${section.label}`
        : `Showing ${section.label} › ${subsection.label}`;
    },
    { enabled }
  );
}

export default useEntityProfileSectionAiCapability;
